import { useEffect, useMemo, useState } from "react";
import api from "../api";

/**
 * Roadmap tab (ROADMAP B8).
 *
 * ROADMAP.md rendered as a progress board so the programme is visible to
 * anyone who logs in, rather than only to whoever opens the repo. The file
 * stays the single source of truth -- this page is read-only, and nothing in
 * the app writes status back. A second place to record progress is a second
 * place for it to be wrong.
 *
 * The backend does the parsing and hands over items, not prose. What people
 * log in wanting to know is how far along things are, which needs counts and
 * grouping; a rendered dump of the markdown would answer a different question.
 */

type Status = "todo" | "doing" | "done" | "dropped" | "idea";

interface Item {
  id: string | null;
  raised?: string;
  title: string;
  owner: string | null;
  detail: string;
  notes: string[];
  status: Status;
  statusLabel: string;
  done: boolean;
}

interface Group {
  letter: string;
  title: string;
  blurb: string;
  items: Item[];
  total: number;
  done: number;
}

interface NoteSection {
  title: string;
  blurb: string;
  notes: string[];
}

interface Board {
  groups: Group[];
  notes: NoteSection[];
  inbox: Item[];
  log: { date: string; notes: string[] }[];
  totals: {
    items: number;
    done: number;
    doing: number;
    todo: number;
    dropped: number;
    inbox: number;
  };
  updated: string;
}

const STATUS_STYLE: Record<Status, string> = {
  done: "bg-brand-tint text-brand-deep",
  doing: "bg-info-tint text-info",
  todo: "bg-paper-2 text-ink-faint",
  dropped: "bg-paper-2 text-ink-faint line-through",
  idea: "bg-sle-tint text-sle",
};

/**
 * The inline markdown the roadmap actually uses: `**bold**`, `*italic*`,
 * `` `code` `` and `[text](href)`. Deliberately not a markdown library --
 * this is four constructs in a file we control, against a dependency tree
 * that already carries ~177 advisories (ROADMAP D2).
 *
 * Everything is emitted as React elements, never as innerHTML, so a stray
 * angle bracket in the file is text rather than markup.
 */
function Inline({ text }: { text: string }) {
  const parts = useMemo(() => {
    const out: React.ReactNode[] = [];
    const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*/g;
    let last = 0;
    let m: RegExpExecArray | null;
    let k = 0;
    while ((m = re.exec(text))) {
      if (m.index > last) out.push(text.slice(last, m.index));
      if (m[1]) {
        const href = m[2];
        const external = /^https?:\/\//.test(href);
        out.push(
          external ? (
            <a
              key={k++}
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className="text-brand-deep underline underline-offset-2"
            >
              {m[1]}
            </a>
          ) : (
            // Repo-relative links (docs/…) have no meaning in the browser, so
            // they render as the path rather than as a link that 404s.
            <code key={k++} className="font-mono text-[12px] bg-paper-2 px-1 py-0.5 rounded">
              {m[1]}
            </code>
          )
        );
      } else if (m[3]) {
        out.push(<strong key={k++} className="font-bold text-ink">{m[3]}</strong>);
      } else if (m[4]) {
        out.push(
          <code key={k++} className="font-mono text-[12px] bg-paper-2 px-1 py-0.5 rounded">
            {m[4]}
          </code>
        );
      } else if (m[5]) {
        out.push(<em key={k++}>{m[5]}</em>);
      }
      last = re.lastIndex;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  }, [text]);
  return <>{parts}</>;
}

function Bar({ done, total }: { done: number; total: number }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div
      className="h-1.5 rounded-full bg-paper-2 overflow-hidden"
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${done} of ${total} complete`}
    >
      <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

function ItemRow({ item }: { item: Item }) {
  const [open, setOpen] = useState(false);
  const hasNotes = item.notes.length > 0;
  return (
    <div className="border-t border-line first:border-t-0 py-3">
      <div className="flex items-start gap-3">
        <span className={`pill shrink-0 mt-0.5 ${STATUS_STYLE[item.status]}`}>{item.statusLabel}</span>
        <div className="min-w-0 flex-1">
          <div className="font-bold flex flex-wrap items-baseline gap-x-2">
            {item.id && <span className="font-mono text-[12px] text-ink-faint">{item.id}</span>}
            <span>{item.title}</span>
            {item.owner && (
              <span className="pill bg-brand-tint text-brand-deep font-semibold">{item.owner}</span>
            )}
            {item.raised && <span className="text-[12px] text-ink-faint">{item.raised}</span>}
          </div>
          {item.detail && (
            <p className="text-ink-soft text-[13px] mt-1 leading-relaxed">
              <Inline text={item.detail} />
            </p>
          )}
          {hasNotes && (
            <>
              <button
                onClick={() => setOpen((v) => !v)}
                className="text-[12px] font-semibold text-brand-deep mt-1.5 hover:underline"
                aria-expanded={open}
              >
                {open ? "Hide" : `${item.notes.length} note${item.notes.length > 1 ? "s" : ""}`}
              </button>
              {open && (
                <ul className="mt-2 space-y-1.5 border-l-2 border-line pl-3">
                  {item.notes.map((n, i) => (
                    <li key={i} className="text-ink-soft text-[13px] leading-relaxed">
                      <Inline text={n} />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RoadmapPage() {
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .get("/roadmap-board")
      .then((r) => live && setBoard(r.data))
      .catch((e) =>
        live && setError(e?.response?.data?.message || "Could not load the roadmap.")
      );
    return () => {
      live = false;
    };
  }, []);

  if (error) {
    return (
      <div className="ui-card p-4 text-[13px] text-wnv bg-wnv-tint border-wnv/20">{error}</div>
    );
  }
  if (!board) {
    return <div className="text-ink-faint text-sm">Loading the roadmap…</div>;
  }

  const { totals } = board;
  const updated = new Date(board.updated).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="space-y-5">
      <section className="ui-card p-5">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="font-extrabold text-xl">Automation Roadmap</h2>
          <span className="text-[12px] text-ink-faint">File updated {updated}</span>
        </div>
        <p className="text-ink-soft text-[13px] mt-1">
          Live from <code className="font-mono text-[12px] bg-paper-2 px-1 py-0.5 rounded">ROADMAP.md</code>.
          Status changes when the file changes — nothing is edited here.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="pill bg-brand-tint text-brand-deep">{totals.done} done</span>
          <span className="pill bg-info-tint text-info">{totals.doing} in progress</span>
          <span className="pill bg-paper-2 text-ink-faint">{totals.todo} not started</span>
          {totals.inbox > 0 && (
            <span className="pill bg-sle-tint text-sle">{totals.inbox} in inbox</span>
          )}
        </div>
        <div className="mt-3">
          <Bar done={totals.done} total={totals.items} />
          <div className="text-[12px] text-ink-faint mt-1.5">
            {totals.done} of {totals.items} programme items complete
          </div>
        </div>
      </section>

      {board.groups.map((g) => (
        <section key={g.letter} className="ui-card p-5">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <h3 className="font-extrabold text-lg">
              <span className="text-ink-faint font-mono text-sm mr-2">{g.letter}</span>
              {g.title}
            </h3>
            <span className="ml-auto text-[12px] font-semibold text-ink-faint">
              {g.done}/{g.total}
            </span>
          </div>
          {g.blurb && <p className="text-ink-soft text-[13px] mt-1">{g.blurb}</p>}
          <div className="mt-2.5">
            <Bar done={g.done} total={g.total} />
          </div>
          <div className="mt-2">
            {g.items.map((it, i) => (
              <ItemRow key={it.id || `${g.letter}-${i}`} item={it} />
            ))}
          </div>
        </section>
      ))}

      {board.inbox.length > 0 && (
        <section className="ui-card p-5">
          <h3 className="font-extrabold text-lg">Inbox</h3>
          <p className="text-ink-soft text-[13px] mt-1">
            Raised but not yet promoted into a lettered group.
          </p>
          <div className="mt-2">
            {board.inbox.map((it, i) => (
              <ItemRow key={i} item={it} />
            ))}
          </div>
        </section>
      )}

      {board.notes.map((n) => (
        <section key={n.title} className="ui-card p-5">
          <h3 className="font-extrabold text-lg">{n.title}</h3>
          {n.blurb && <p className="text-ink-soft text-[13px] mt-1">{n.blurb}</p>}
          <ul className="mt-3 space-y-2">
            {n.notes.map((t, i) => (
              <li key={i} className="text-ink-soft text-[13px] leading-relaxed border-l-2 border-line pl-3">
                <Inline text={t} />
              </li>
            ))}
          </ul>
        </section>
      ))}

      {board.log.length > 0 && (
        <section className="ui-card p-5">
          <h3 className="font-extrabold text-lg">Log</h3>
          <p className="text-ink-soft text-[13px] mt-1">Newest first.</p>
          <div className="mt-3 space-y-4">
            {board.log.map((e) => (
              <div key={e.date}>
                <div className="font-mono text-[12px] font-semibold text-ink-faint">{e.date}</div>
                <ul className="mt-1.5 space-y-2">
                  {e.notes.map((t, i) => (
                    <li
                      key={i}
                      className="text-ink-soft text-[13px] leading-relaxed border-l-2 border-line pl-3"
                    >
                      <Inline text={t} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
